<template>
<v-card class="mx-auto" width="90%" style="margin-bottom: 15px;">
  <v-dialog v-model="editDialog" max-width="400">
    <v-card>
      <v-card-title class="headline">{{ $t('views.Documents.editDetailsTitle') }}</v-card-title>
      <v-card-text>
        {{ editItem.name }}
        <v-text-field v-model="itemDetails"></v-text-field>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="gray darken-1" text @click="editDialog = false">{{ $t('views.Documents.cancel') }}</v-btn>
        <v-btn color="red darken-1" text @click="triggerEdit(editItem, itemDetails); editDialog = false">{{ $t('views.Documents.save') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  <v-dialog v-model="uploadDialog" width="500" persistent>
    <v-card>
      <v-card-title
        class="grey lighten-2"
        primary-title
      >
        {{ $t('views.Documents.uploadingFile') }} {{ !!currentFile ? currentFile.name : '' }}
      </v-card-title>
      <v-card-text v-if="!!currentFile && !! uploadFileProgress">
        <v-progress-linear v-model="uploadFileProgress.percent" height="25" reactive class="mt-5">
          <strong>{{ uploadFileProgress.percent }}% ({{ `${fileSizeToString(uploadFileProgress.size)}/${fileSizeToString(currentFile.size)}` }})</strong>
        </v-progress-linear>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          text
          @click="uploadFileProgress.task.cancel(); uploadDialog = false"
        >
          {{ $t('views.Documents.cancel') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  <v-container fluid class="my-5">
    <v-layout row justify-center>
      <v-row>
        <v-col cols="12">
          <v-data-table
            :headers="headers"
            :items="modelFiles"
            style="width: 100%;"
            sort-by="created"
            :sort-desc="true"
            :search="search"
            :loading-text="$t('views.Documents.loadingText')"
            :must-sort="true"
            :no-data-text="$t('views.Documents.noDataText')"
          >
            <template v-slot:top>
              <v-toolbar flat color="white">
                <v-btn small color="#212121" class="white--text" @click="selectFile" v-if="authUser.isAdmin || authUser.isScientist || authUser.isUploader">
                  <input v-show="false" type="file" multiple @change="previewFile" id="file" ref="file" />
                  <v-icon class="align-center" color="primary">mdi-upload</v-icon>
                  <span class="pl-2">{{ $t('views.Documents.uploadDocument') }}</span>
                </v-btn>
                <v-spacer></v-spacer>
                <v-text-field
                  v-model="search"
                  append-icon="mdi-magnify"
                  :label="$t('views.Documents.search')"
                  single-line
                  hide-details
                ></v-text-field>
              </v-toolbar>
            </template>
            <template v-slot:item.name="{ item }">
              <v-icon>{{ fileTypeToIcon(item.contentType) }}</v-icon> {{ item.name }}
            </template>
            <template v-slot:item.created="{ item }">
              {{ formatDate(item.created) }}
            </template>
            <template v-slot:item.size="{ item }">
              {{ fileSizeToString(item.size) }}
            </template>
            <template v-slot:item.action="{ item }">
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn icon v-on="on" :href="item.url" target="_blank">
                    <v-icon>mdi-download</v-icon>
                  </v-btn>
                </template>
                {{ $t('views.Documents.download') }}
              </v-tooltip>
              <v-tooltip top v-if="authUser.isAdmin || authUser.isScientist || authUser.isUploader">
                <template v-slot:activator="{ on }">
                  <v-btn icon v-on="on" @click.stop="editDialog = true; editItem = item; item.customMetadata && 'details' in item.customMetadata ? itemDetails = item.customMetadata.details : itemDetails = ''">
                    <v-icon>mdi-pencil</v-icon>
                  </v-btn>
                </template>
                {{ $t('views.Documents.editDetails') }}
              </v-tooltip>
              <v-tooltip top v-if="authUser.isAdmin || authUser.isScientist || authUser.isUploader">
                <template v-slot:activator="{ on }">
                  <v-btn icon v-on="on" @click.stop="deleteDialog = true; deleteItem = item">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                  <v-dialog v-model="deleteDialog" max-width="400">
                    <v-card>
                      <v-card-title class="headline">{{ $t('views.Documents.warningTitle') }}</v-card-title>
                      <v-card-text>
                        <div> {{ deleteMessage }} </div>
                        <br/>
                        {{ $t('views.Documents.deleting') }} <b>{{ deleteItem.name }}</b>
                        <br/>
                        {{ $t('views.Documents.deleteConfirmation') }}
                      </v-card-text>
                      <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn color="gray darken-1" text @click="deleteDialog = false">{{ $t('views.Documents.cancel') }}</v-btn>
                        <v-btn color="red darken-1" text @click="triggerDelete(deleteItem); deleteDialog = false">{{ $t('views.Documents.delete') }}</v-btn>
                      </v-card-actions>
                    </v-card>
                  </v-dialog>
                </template>
                {{ $t('views.Documents.delete') }}
              </v-tooltip>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
      <v-snackbar v-model="snack" :timeout="3000" :color="snackColor">
        {{ snackText }}
      </v-snackbar>
    </v-layout>
  </v-container>
</v-card>
</template>

<script>
import numbro from 'numbro'
import moment from 'moment'
import { mapActions, mapGetters } from 'vuex'

export default {
  data () {
    return {
      search: '',
      deleteDialog: false,
      deleteItem: '',
      editDialog: false,
      editItem: '',
      itemDetails: '',
      deleteMessage: '',
      snack: false,
      snackColor: '',
      snackText: '',
      files: [],
      currentFile: null,
      uploadDialog: false
    }
  },
  methods: {
    ...mapActions('storage', [
      'loadModelFiles',
      'deleteModelFile',
      'editModelFile',
      'uploadFile',
      'resetUploadFileProgress'
    ]),
    triggerEdit (item, details) {
      this.editModelFile({
        clientId: this.currentClient,
        filename: item.name,
        details: details
      }).then(result => {
        this.snackColor = 'green'
        this.snackText = this.$t('views.Documents.fileEdited')
        this.snack = true
        this.loadModelFiles({
          modelId: this.currentClient
        })
      }).catch(error => {
        this.snackColor = 'red'
        this.snackText = error.message
        this.snack = true
      })
    },
    triggerDelete (item) {
      this.snackColor = 'orange'
      this.snackText = this.$t('views.Documents.deletingFile')
      this.snack = true
      this.deleteModelFile({
        clientId: this.currentClient,
        filename: item.name
      }).then(result => {
        this.snackColor = 'green'
        this.snackText = this.$t('views.Documents.fileDeleted')
        this.snack = true
        this.loadModelFiles({
          modelId: this.currentClient
        })
      }).catch(error => {
        this.snackColor = 'red'
        this.snackText = error.message
        this.snack = true
      })
    },
    formatValue: function (value) {
      return numbro(value).format({
        average: true,
        lowPrecision: false,
        mantissa: 1,
        optionalMantissa: true
      })
    },
    formatDate (date) {
      if (date) {
        return moment(date).format('MMM D, YYYY')
      } else {
        return ''
      }
    },
    selectFile: function () {
      this.$refs.file.click()
    },
    previewFile: function () {
      this.files = []
      let allowedFileTypes = [
        'application/zip',
        'application/x-zip-compressed',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/pdf'
      ]
      if (this.authUser.isAdmin || this.authUser.isScientist) {
        allowedFileTypes.push('text/html')
      }
      for (let index = 0; index < this.$refs.file.files.length; index++) {
        const file = this.$refs.file.files[index]
        if (!allowedFileTypes.includes(file.type)) {
          // File type not allowed for uploads (security requirement not to allow executable file uploads)
          this.snackColor = 'red'
          this.snackText = this.$t('views.Documents.uploadError')
          this.snack = true
          return
        }
        this.files.push(file)
      }
      this.triggerUploadFiles()
    },
    triggerUploadFiles: async function () {
      this.uploadDialog = true
      for (const file of this.files) {
        this.currentFile = file
        await this.triggerUploadFile(file)
        this.currentFile = null
      }
      this.loadModelFiles({
        modelId: this.currentClient
      })
      this.uploadDialog = false
    },
    triggerUploadFile: function (file) {
      return new Promise((resolve, reject) => {
        this.uploadFile({
          file: file,
          path: `files/${this.currentClient}/${file.name}`,
          customMetadata: {
            'uploadedBy': this.authUser.email
          }
        }).then(() => {
          resolve()
        }, error => {
          alert(this.$t('views.Documents.uploadError'))
          reject(error)
        })
      })
    },
    fileSizeToString: function (size) {
      return numbro(size).format({
        output: 'byte',
        base: 'binary',
        mantissa: 1
      })
    },
    fileTypeToIcon: function (type) {
      if (!type) {
        return 'mdi-file'
      }
      if (type.includes('spreadsheet')) {
        return 'mdi-file-excel'
      } else if (type.includes('application/pdf')) {
        return 'mdi-file-pdf'
      } else if (type.includes('application/msword')) {
        return 'mdi-file-word'
      } else if (type.includes('image/')) {
        return 'mdi-file-image'
      } else {
        return 'mdi-file'
      }
    }
  },
  computed: {
    ...mapGetters('auth', [
      'authUser'
    ]),
    ...mapGetters('storage', [
      'modelFiles',
      'uploadFileProgress'
    ]),
    ...mapGetters('clients', [
      'currentClient'
    ]),
    headers () {
      return [
        { text: this.$t('views.Documents.headerName'), value: 'name', align: 'start' },
        { text: this.$t('views.Documents.headerDetails'), value: 'customMetadata.details', sortable: false },
        { text: this.$t('views.Documents.headerSize'), value: 'size' },
        { text: this.$t('views.Documents.headerCreated'), value: 'created', width: '120px' },
        { text: this.$t('views.Documents.headerActions'), value: 'action', sortable: false, width: '140px' }
      ]
    }
  },
  created: function () {
    this.loadModelFiles({
      modelId: this.currentClient
    })
  }
}
</script>
